import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eatDayOf, formatEatTime, formatIsoDay } from '@wodi/domain';
import type { AppointmentKind, AppointmentView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  DateField,
  LoadingBlock,
  Notice,
  RadioGroup,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  appointmentKeys,
  bookAppointment,
  patientAppointmentsQuery,
  rescheduleAppointment,
  schedulesQuery,
  slotsQuery,
} from '../api/appointments.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientSearchPicker } from '../patients/patient-search-palette.tsx';
import { errorText } from '../stock/common.tsx';
import styles from './appointments.module.css';

// Booking and moving an appointment (docs/plans/phase2-appointments.md §6): schedule, day,
// then a free time; for a booking, also the kind, a note for staff and the patient's
// answer about SMS reminders.

export type BookingMode =
  | {
      kind: 'book';
      /** Fixed when booking from a patient's record or a consultation. */
      patient?: { id: string; displayName: string };
      /** A follow-up from the consultation: its visit, and the clinician's own schedule first. */
      followUp?: { sourceVisitId: string; practitionerUserId: string | null };
    }
  | { kind: 'reschedule'; appointment: AppointmentView };

export function todayIso(): string {
  return formatIsoDay(eatDayOf(new Date()));
}

const KIND_OPTIONS = [
  { id: 'new', label: 'New' },
  { id: 'follow_up', label: 'Follow-up' },
];

function SmsChoice({
  patientId,
  value,
  onChange,
}: {
  patientId: string;
  value: 'granted' | 'refused' | null;
  onChange: (v: 'granted' | 'refused') => void;
}) {
  const info = useQuery(patientAppointmentsQuery(patientId));
  if (info.isPending) return <LoadingBlock label="Loading SMS reminder details" rows={1} />;
  if (info.isError) {
    return <Notice tone="error">{errorText(info.error, "SMS details couldn't be loaded.")}</Notice>;
  }
  const { mobileLastDigits, reminderConsent } = info.data;
  if (mobileLastDigits === null) {
    return (
      <p className={styles.meta}>
        SMS reminders: no mobile number on file, so none will be sent. Add one on the patient record
        to offer reminders.
      </p>
    );
  }
  const current =
    reminderConsent.decision === 'granted'
      ? 'The patient said yes last time.'
      : reminderConsent.decision === null
        ? 'Not asked yet.'
        : 'The patient said no last time.';
  return (
    <RadioGroup
      label={`Send SMS reminders to the number ending ${mobileLastDigits}?`}
      description={`${current} The SMS gives only the facility, date and time.`}
      orientation="horizontal"
      value={value ?? (reminderConsent.decision === 'granted' ? 'granted' : '')}
      onChange={(v) => {
        onChange(v === 'granted' ? 'granted' : 'refused');
      }}
      options={[
        { id: 'granted', label: 'Yes' },
        { id: 'refused', label: 'No' },
      ]}
    />
  );
}

export function BookingDialog({
  mode,
  isOpen,
  onOpenChange,
  onDone,
  initialDay,
}: {
  mode: BookingMode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: (appointment: AppointmentView) => void;
  /** The day to start on when booking; today by default. */
  initialDay?: string;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const schedules = useQuery(schedulesQuery);
  const existing = mode.kind === 'reschedule' ? mode.appointment : null;
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [patient, setPatient] = useState<{ id: string; displayName: string } | null>(
    mode.kind === 'book'
      ? (mode.patient ?? null)
      : { id: mode.appointment.patient.id, displayName: mode.appointment.patient.displayName },
  );
  const [search, setSearch] = useState('');
  const [chosenSchedule, setChosenSchedule] = useState<string | null>(
    existing?.schedule.id ?? null,
  );
  const [day, setDay] = useState<string | null>(
    existing === null
      ? (initialDay ?? todayIso())
      : formatIsoDay(eatDayOf(new Date(existing.startsAt))),
  );
  const [startsAt, setStartsAt] = useState<string | null>(null);
  const [kind, setKind] = useState<AppointmentKind>(
    mode.kind === 'book' && mode.followUp !== undefined ? 'follow_up' : 'new',
  );
  const [note, setNote] = useState('');
  const [sms, setSms] = useState<'granted' | 'refused' | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);

  const active = schedules.data?.items ?? [];
  const preferred =
    mode.kind === 'book' && mode.followUp !== undefined
      ? active.find((s) => s.practitioner?.userId === mode.followUp?.practitionerUserId)
      : undefined;
  const scheduleId = chosenSchedule ?? preferred?.id ?? active[0]?.id ?? null;
  const slots = useQuery({
    ...slotsQuery(scheduleId ?? '', day ?? ''),
    enabled: scheduleId !== null && day !== null,
  });
  const free = (slots.data?.slots ?? []).filter((s) => !s.past && s.free > 0);

  const save = useMutation({
    mutationFn: async () => {
      if (scheduleId === null || startsAt === null || patient === null) {
        throw new Error('incomplete');
      }
      if (mode.kind === 'reschedule') {
        return rescheduleAppointment(mode.appointment.id, {
          scheduleId,
          startsAt,
          reason: reason.trim(),
        });
      }
      return bookAppointment({
        clientRequestId,
        patientId: patient.id,
        scheduleId,
        startsAt,
        kind,
        ...(mode.followUp === undefined ? {} : { sourceVisitId: mode.followUp.sourceVisitId }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        ...(sms === null ? {} : { smsReminders: sms }),
      });
    },
    onSuccess: async (appointment) => {
      await queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      onDone(appointment);
    },
  });

  const reasonShort = mode.kind === 'reschedule' && reason.trim().length < 5;
  const missing =
    patient === null
      ? 'Choose the patient.'
      : scheduleId === null
        ? 'Choose a schedule.'
        : startsAt === null
          ? 'Choose a time.'
          : reasonShort
            ? 'Give a reason of at least 5 characters.'
            : null;
  const submit = () => {
    setTried(true);
    if (missing === null && online) save.mutate();
  };
  const title = mode.kind === 'reschedule' ? 'Move appointment' : 'Book appointment';

  return (
    <Dialog title={title} isOpen={isOpen} onOpenChange={onOpenChange}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {patient === null ? (
          <PatientSearchPicker
            autoFocus
            inputValue={search}
            onInputChange={setSearch}
            onSelect={(result) => {
              setSearch('');
              setPatient({ id: result.id, displayName: result.displayName });
            }}
          />
        ) : (
          <div className={styles.row}>
            <span>
              <span className={styles.meta}>Patient</span>
              <span className={styles.patientName}>{patient.displayName}</span>
            </span>
            {mode.kind === 'book' && mode.patient === undefined ? (
              <Button
                dense
                variant="quiet"
                onPress={() => {
                  setPatient(null);
                  setSms(null);
                }}
              >
                Change patient
              </Button>
            ) : null}
          </div>
        )}
        {schedules.isPending ? <LoadingBlock label="Loading schedules" rows={1} /> : null}
        {schedules.isError ? (
          <Notice tone="error">
            {errorText(schedules.error, "The schedules couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {schedules.isSuccess && active.length === 0 ? (
          <Notice tone="info">
            No appointment schedules are set up at this facility. An administrator can add them in
            Settings, under Appointments.
          </Notice>
        ) : null}
        {active.length > 0 ? (
          <div className={styles.row}>
            <Select
              label="Schedule"
              options={active.map((s) => ({
                id: s.id,
                label: s.practitioner === null ? s.name : `${s.name} (${s.practitioner.name})`,
              }))}
              value={scheduleId}
              onChange={(key) => {
                setChosenSchedule(String(key));
                setStartsAt(null);
              }}
            />
            <DateField
              label="Day"
              value={day}
              minValue={todayIso()}
              onChange={(v) => {
                setDay(v);
                setStartsAt(null);
              }}
            />
          </div>
        ) : null}
        {scheduleId !== null && day !== null ? (
          slots.isPending ? (
            <LoadingBlock label="Loading free times" rows={1} />
          ) : slots.isError ? (
            <Notice tone="error">
              {errorText(slots.error, "The free times couldn't be loaded. Try again.")}
            </Notice>
          ) : free.length === 0 ? (
            <Notice tone="info">No free times on this day. Choose another day or schedule.</Notice>
          ) : (
            <RadioGroup
              label="Time"
              orientation="horizontal"
              value={startsAt ?? ''}
              onChange={setStartsAt}
              options={free.map((s) => ({
                id: s.startsAt,
                label: formatEatTime(new Date(s.startsAt)),
              }))}
              {...(tried && startsAt === null ? { errorMessage: 'Choose a time.' } : {})}
            />
          )
        ) : null}
        {mode.kind === 'book' ? (
          <>
            <RadioGroup
              label="Kind"
              orientation="horizontal"
              value={kind}
              onChange={(v) => {
                setKind(v === 'follow_up' ? 'follow_up' : 'new');
              }}
              options={KIND_OPTIONS}
            />
            <TextField
              label="Note for staff (optional)"
              description="Not sent to the patient."
              value={note}
              onChange={setNote}
              maxLength={200}
            />
            {patient === null ? null : (
              <SmsChoice patientId={patient.id} value={sms} onChange={setSms} />
            )}
          </>
        ) : (
          <TextArea
            label="Reason"
            description="Kept in the appointment's history."
            value={reason}
            onChange={setReason}
            {...(tried && reasonShort
              ? { errorMessage: 'Give a reason of at least 5 characters.' }
              : {})}
          />
        )}
        {save.isError ? (
          <Notice tone="error">
            {errorText(save.error, "The appointment couldn't be saved. Try again.")}
          </Notice>
        ) : null}
        {tried && missing !== null && !save.isError ? <Notice tone="info">{missing}</Notice> : null}
        {online ? null : (
          <Notice tone="info">
            Booking needs a connection to Wodi. Reconnect, then try again.
          </Notice>
        )}
        <DialogActions>
          <Button
            variant="quiet"
            onPress={() => {
              onOpenChange(false);
            }}
          >
            Close
          </Button>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={save.isPending}
            pendingLabel={mode.kind === 'reschedule' ? 'Moving…' : 'Booking…'}
          >
            {title}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
